import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Box, Typography, Button, TextField, MenuItem, Chip, Grid, CircularProgress, Tooltip,
    Dialog, DialogContent, DialogActions,
} from '@mui/material';
import { createProgramRequestService, listProgramRequestsService } from '../../../services/client.service';
import { toast } from '../../../utils/utils';
import { TOAST_TYPES, WORKSPACE_REQUEST_STATUS } from '../../../utils/constants';
import {getProgramPreviewPath} from "./DQProProgPreviewRedirect.page";

const NAVY = 'var(--brand-text, #052F44)';
const ORANGE = '#FF6900';

// Orange primary action. `color` must be set explicitly: overriding
// backgroundColor in sx leaves the label on the theme's dark contrastText,
// which is why these buttons render with black text otherwise. Same fix the
// app's other orange buttons use (see PublishProgramDialogs).
const primaryBtnSx = {
    textTransform: 'none', backgroundColor: ORANGE, color: '#fff',
    '&:hover': { backgroundColor: '#e55e00' },
    '&.Mui-disabled': { backgroundColor: ORANGE, color: '#fff', opacity: 0.6 },
};

// Preset session durations. Picking "Other" turns the SAME field into a type
// input (slide 265: "Choose an option below … or enter a custom format").
const DURATION_OPTIONS = ['30', '45', '60', '90'];
const DURATION_OTHER = '__other__';

// Where the school reviews programs / previews a sample — the Learning Programs
// list page. "See Programs", "Click to view sample program", and a delivered
// request's "View Program" all land here.
const PROGRAMS_LIST_PATH = '/dq_pro/learning/program/list';
// Opening ONE program uses getProgramPreviewPath — same route the owner's program
// list uses for "View Program", so the school lands on the delivered program itself.

// Pager buttons — brand orange in both states so paging never recolors them.
const navBtnSx = {
    textTransform: 'none', fontSize: '12.5px', color: ORANGE, borderColor: ORANGE,
    '&:hover': { borderColor: '#e55e00', backgroundColor: 'rgba(255,105,0,0.08)' },
    '&.Mui-disabled': { color: ORANGE, borderColor: ORANGE, opacity: 0.4 },
};

const STATUS_CHIP = {
    [WORKSPACE_REQUEST_STATUS.PENDING]:     { label: 'Pending',      bg: '#EFA020' },
    [WORKSPACE_REQUEST_STATUS.IN_PROGRESS]: { label: 'In Progress',  bg: '#2563EB' },
    [WORKSPACE_REQUEST_STATUS.BUILT]:       { label: 'Delivered',    bg: '#1B6B55' },
    [WORKSPACE_REQUEST_STATUS.DECLINED]:    { label: 'Declined',     bg: '#9CA3AF' },
};

const EMPTY = {
    topic: '', grade_level: '', num_sessions: '',
    session_duration_mins: '',
    local_sensitivities: '', school_subject: '',
};

const SchoolProgramRequest = () => {
    const history = useHistory();
    const [form, setForm] = useState(EMPTY);
    const [submitting, setSubmitting] = useState(false);
    const [thanksOpen, setThanksOpen] = useState(false);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    // true once "Other" is picked → the duration cell becomes a type-in field
    // (in place — no separate input appears).
    const [durationCustom, setDurationCustom] = useState(false);

    // "Your Requests" — server-side search + pagination.
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [search, setSearch] = useState('');
    const [searchApplied, setSearchApplied] = useState('');
    const debounceRef = useRef(null);

    // Capture the value BEFORE the functional updater runs — React 16 pools
    // synthetic events, so reading e.target inside setForm(...) nullifies it.
    const set = (k) => (e) => {
        const value = e.target.value;
        setForm((p) => ({ ...p, [k]: value }));
        setErrors((prev) => (prev[k] ? { ...prev, [k]: undefined } : prev));
    };

    // Resolved duration in minutes from the single free-type field (preset or
    // custom, both live in session_duration_mins now).
    const resolvedDuration = () => {
        const n = Number(String(form.session_duration_mins || '').trim());
        return Number.isFinite(n) && n > 0 ? n : null;
    };

    // Required fields (slide 159): topic, grade, # sessions, duration.
    const validate = () => {
        const e = {};
        if (!String(form.topic || '').trim()) e.topic = 'Please enter the topic/area you want to teach.';
        if (!String(form.grade_level || '').trim()) e.grade_level = 'Grade level is required.';
        const n = Number(form.num_sessions);
        if (!form.num_sessions || !Number.isFinite(n) || n < 1) e.num_sessions = 'Enter at least 1 session.';
        if (!String(form.session_duration_mins || '').trim()) {
            e.session_duration_mins = 'Enter or pick a session duration (minutes).';
        } else if (!resolvedDuration()) {
            e.session_duration_mins = 'Duration must be a number of minutes.';
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await listProgramRequestsService({
                search: searchApplied, page: page + 1, limit: rowsPerPage,
            });
            const data = (res && res.data) || {};
            setRows(Array.isArray(data.requests) ? data.requests : []);
            setTotal(data.total || 0);
        } catch { setRows([]); setTotal(0); }
        setLoading(false);
    }, [searchApplied, page, rowsPerPage]);

    useEffect(() => { load(); }, [load]);

    // Debounced server-side search — resets to page 1.
    const onSearchChange = (e) => {
        const val = e.target.value;
        setSearch(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => { setPage(0); setSearchApplied(val.trim()); }, 400);
    };

    const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));

    const submit = async () => {
        if (!validate()) {
            toast(TOAST_TYPES.WARN, 'Request', 'Please fill in the required fields.');
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                topic: form.topic,
                grade_level: form.grade_level,
                num_sessions: Number(form.num_sessions),
                session_duration_mins: resolvedDuration(),
                local_sensitivities: form.local_sensitivities,
                school_subject: form.school_subject,
            };
            const res = await createProgramRequestService(payload);
            if (res && res.data) {
                setThanksOpen(true);
                setForm(EMPTY);
                setErrors({});
                setDurationCustom(false);
                await load();
            }
        } catch {
            /* invokeApi toasts on failure */
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, mx: 'auto' }}>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color: NAVY }}>Deliver Learning</Typography>
            <Typography sx={{ fontSize: 14, color: 'var(--brand-text-muted, #6A7282)', mt: 0.5, mb: 3 }}>
                Request a learning program tailored to your school, or review the programs already linked to you.
            </Typography>

            <Grid container spacing={2.5} alignItems="stretch">
                {/* ── Card 1 — Request a Learning Program (slide 159/160) ── */}
                <Grid item xs={12} md={7}>
                    <Box sx={{ border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', p: 3, height: '100%', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                        <Typography sx={{ fontSize: 17, fontWeight: 700, color: NAVY, mb: 0.5 }}>Request a Learning Program</Typography>
                        <Typography sx={{ fontSize: 13.5, color: 'var(--brand-text-muted, #6A7282)', mb: 2.5 }}>
                            Have a topic you want to teach your students? Type in a request below along with how many
                            sessions you need and how long you want each session to be. Then, your DQ Partner will
                            create the program for you.
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField fullWidth required size="small" label="Type the topic/area you want to teach"
                                    value={form.topic} onChange={set('topic')} placeholder="e.g. Managing Cyberbullying"
                                    error={!!errors.topic} helperText={errors.topic || ''} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField fullWidth required size="small" label="Grade Level"
                                    value={form.grade_level} onChange={set('grade_level')} placeholder="e.g. Grade 7"
                                    error={!!errors.grade_level} helperText={errors.grade_level || ''} />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField fullWidth required size="small" type="number" label="# of Sessions"
                                    inputProps={{ min: 1 }} value={form.num_sessions} onChange={set('num_sessions')} placeholder="e.g. 5"
                                    error={!!errors.num_sessions} helperText={errors.num_sessions || ''} />
                            </Grid>
                            {/* Same cell: a preset dropdown, OR — once "Other" is
                                picked — a type-in field IN PLACE (no new field). */}
                            <Grid item xs={12} sm={5}>
                                {!durationCustom ? (
                                    <TextField fullWidth required select size="small" label="Duration of Each Session"
                                        value={DURATION_OPTIONS.includes(String(form.session_duration_mins)) ? String(form.session_duration_mins) : ''}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            if (v === DURATION_OTHER) {
                                                setDurationCustom(true);
                                                setForm((p) => ({ ...p, session_duration_mins: '' }));
                                            } else {
                                                setForm((p) => ({ ...p, session_duration_mins: v }));
                                                setErrors((prev) => (prev.session_duration_mins ? { ...prev, session_duration_mins: undefined } : prev));
                                            }
                                        }}
                                        error={!!errors.session_duration_mins} helperText={errors.session_duration_mins || ''}>
                                        {DURATION_OPTIONS.map((d) => <MenuItem key={d} value={d}>{d} minutes</MenuItem>)}
                                        <MenuItem value={DURATION_OTHER}>Other (type your own)</MenuItem>
                                    </TextField>
                                ) : (
                                    <TextField fullWidth required size="small" type="number" autoFocus
                                        label="Duration of Each Session (mins)" placeholder="e.g. 25"
                                        inputProps={{ min: 1 }}
                                        value={form.session_duration_mins} onChange={set('session_duration_mins')}
                                        error={!!errors.session_duration_mins}
                                        helperText={errors.session_duration_mins || ''}
                                        InputProps={{
                                            endAdornment: (
                                                <Button size="small" onClick={() => { setDurationCustom(false); setForm((p) => ({ ...p, session_duration_mins: '' })); }}
                                                    sx={{ textTransform: 'none', minWidth: 0, px: 0.5, color: ORANGE, fontSize: 12, whiteSpace: 'nowrap' }}>
                                                    Presets
                                                </Button>
                                            ),
                                        }} />
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY, mt: 1 }}>Delivery Requirements</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth size="small" multiline minRows={2} label="Local sensitivities (optional)"
                                    value={form.local_sensitivities} onChange={set('local_sensitivities')}
                                    placeholder="If you have any local sensitivities, type them here." />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth size="small" label="Connect to a school subject (optional)"
                                    value={form.school_subject} onChange={set('school_subject')}
                                    placeholder="e.g. math class, biology, physics, music" />
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Button variant="contained" disabled={submitting} onClick={submit}
                                    sx={{ ...primaryBtnSx, px: 3 }}>
                                    {submitting ? 'Submitting…' : 'Request a Program'}
                                </Button>
                                {/* Slide 160 — "Click to view sample program". */}
                                <Button variant="text" onClick={() => history.push(PROGRAMS_LIST_PATH)}
                                    sx={{ textTransform: 'none', color: ORANGE, fontSize: 13.5 }}>
                                    Click to view sample program
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>

                {/* ── Card 2 — Your Available Learning Programs (slide 160) ── */}
                <Grid item xs={12} md={5}>
                    <Box sx={{ border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', p: 3, height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                        <Typography sx={{ fontSize: 17, fontWeight: 700, color: NAVY, mb: 0.5 }}>Your Available Learning Programs</Typography>
                        <Typography sx={{ fontSize: 13.5, color: 'var(--brand-text-muted, #6A7282)', mb: 2 }}>
                            Check all the learning programs that are linked to your school. You can link them to any
                            class by going to “Your Classes” and linking them individually.
                        </Typography>
                        <Box sx={{ mt: 'auto' }}>
                            <Button variant="outlined" onClick={() => history.push(PROGRAMS_LIST_PATH)}
                                sx={{ textTransform: 'none', color: ORANGE, borderColor: ORANGE, px: 3, '&:hover': { borderColor: '#e55e00', backgroundColor: 'rgba(255,105,0,0.08)' } }}>
                                See Programs
                            </Button>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* This school's own requests + their status. */}
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: NAVY, mt: 4, mb: 1.5 }}>
                Your Requests{total ? ` (${total})` : ''}
            </Typography>
            {/* Search sits on the LEFT, same as the owner's requests page. */}
            <Box sx={{ mb: 2, maxWidth: 380 }}>
                <TextField fullWidth size="small" value={search} onChange={onSearchChange}
                    placeholder="Search by topic or grade…"
                    sx={{ backgroundColor: 'var(--brand-surface, #fff)' }} />
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={26} /></Box>
            ) : rows.length === 0 ? (
                <Typography sx={{ fontSize: 14, color: 'var(--brand-text-muted, #9CA3AF)' }}>
                    {searchApplied ? 'No requests match your search.' : 'You haven’t requested any programs yet.'}
                </Typography>
            ) : (
                <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {rows.map((r) => {
                            const st = STATUS_CHIP[r.status] || STATUS_CHIP[WORKSPACE_REQUEST_STATUS.PENDING];
                            // The program only exists once the partner publishes it, so the
                            // action stays disabled until the request is Delivered AND the
                            // built program is known — then it opens THAT program directly.
                            const isDelivered = Number(r.status) === WORKSPACE_REQUEST_STATUS.BUILT;
            // Openable once the partner has LINKED it to this school. Only an
            // explicit `false` blocks it — if the API can't report link state the
            // button stays usable rather than locking the school out of a program
            // that was delivered to them.
                            const canOpenProgram = isDelivered && !!r.program_code && r.program_linked !== false;
                            return (
                                <Box key={r._id} sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                                    border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '10px', p: 2,
                                    backgroundColor: 'var(--brand-surface, #fff)', flexWrap: 'wrap',
                                }}>
                                    <Box sx={{ minWidth: 200 }}>
                                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY }}>{r.topic || '—'}</Typography>
                                        <Typography sx={{ fontSize: 12, color: 'var(--brand-text-muted, #6A7282)' }}>
                                            {[r.grade_level, r.num_sessions ? `${r.num_sessions} × ${r.session_duration_mins || '?'} mins` : null].filter(Boolean).join(' · ') || '—'}
                                        </Typography>
                                        {Number(r.status) === WORKSPACE_REQUEST_STATUS.DECLINED && r.decline_reason ? (
                                            <Typography sx={{ fontSize: 12, color: '#B45309', mt: 0.5 }}>
                                                Reason: {r.decline_reason}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Chip label={st.label} size="small" sx={{ backgroundColor: st.bg, color: '#fff', fontWeight: 700 }} />
                                        {/* View Program only makes sense while a program can still
                                            arrive: Pending → disabled (with why), Delivered → enabled.
                                            A DECLINED request will never have a program, so no button. */}
                                        {Number(r.status) !== WORKSPACE_REQUEST_STATUS.DECLINED && (
                                            <Tooltip
                                                title={canOpenProgram
                                                    ? ''
                                                    : isDelivered
                                                        ? 'Your DQ Partner is linking this program to your school.'
                                                        : 'Available once your DQ Partner publishes this program.'}
                                                arrow
                                            >
                                                <span>
                                                    <Button size="small" variant="outlined" disabled={!canOpenProgram}
                                                        onClick={() => history.push(getProgramPreviewPath(r.program_code))}
                                                        sx={{
                                                            textTransform: 'none', whiteSpace: 'nowrap',
                                                            color: ORANGE, borderColor: ORANGE,
                                                            '&:hover': { borderColor: '#e55e00', backgroundColor: 'rgba(255,105,0,0.08)' },
                                                        }}>
                                                        View Program
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Server-side pagination. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, gap: 1, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontSize: 12.5, color: 'var(--brand-text-muted, #6B7280)' }}>Rows per page:</Typography>
                            <TextField select size="small" value={rowsPerPage}
                                onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                                sx={{ width: 76, backgroundColor: 'var(--brand-surface, #fff)' }}>
                                {[5, 10, 25, 50].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                            </TextField>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontSize: 12.5, color: 'var(--brand-text-muted, #6B7280)' }}>{`Page ${page + 1} of ${totalPages}`}</Typography>
                            <Button size="small" variant="outlined" sx={navBtnSx} disabled={loading || page <= 0}
                                onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</Button>
                            <Button size="small" variant="outlined" sx={navBtnSx} disabled={loading || page + 1 >= totalPages}
                                onClick={() => setPage((p) => p + 1)}>Next</Button>
                        </Box>
                    </Box>
                </>
            )}

            {/* Slide 245 — simple confirmation: message + OK, nothing else. */}
            <Dialog open={thanksOpen} onClose={() => setThanksOpen(false)} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: '18px' } }}>
                <DialogContent sx={{ textAlign: 'center', pt: 5, pb: 2, px: 4 }}>
                    <Typography sx={{ fontSize: 17, color: 'var(--brand-text, #374151)', lineHeight: 1.5 }}>
                        Thank you! We will forward your request to your DQ Partner to create your program.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 4, pt: 1 }}>
                    <Button variant="contained" onClick={() => setThanksOpen(false)}
                        sx={{ ...primaryBtnSx, px: 5, borderRadius: '8px', fontWeight: 700 }}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SchoolProgramRequest;
